import type { FC } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Stack } from '@mui/material';

import { SECTION } from 'src/constants/section';
import type { ActionItemSelection } from 'src/types/myFollowUp';
import useUserPermissions from 'src/components/app/context/UserContext/useUserPermissions';
import useMyFollowUp from 'src/components/myFollowUp/context/MyFollowUpContext';
import { FollowUpTableProvider } from 'src/components/myFollowUp/MyFollowUpPage/FollowUpOverview/FollowUpTable/FollowUpTableContext';
import TicketDetailModal from 'src/components/myFollowUp/MyFollowUpPage/FollowUpOverview/TicketDetailModal';

import CreateActionItemModalButton from '../CreateMyFollowUpModal/CreateActionItemModalButton';

import { FollowUpDetailProvider } from './FollowUpDetailModal/FollowUpDetailContext';
import FollowUpDetailModal from './FollowUpDetailModal/FollowUpDetailModal';
import FollowUpTable from './FollowUpTable';

const FollowUpOverview: FC = () => {
  const { t } = useTranslation('myFollowUp');
  const { downloadAll } = useMyFollowUp();
  const {
    hasWritePermissionForModuleTypeInAnySurvey,
    hasAdminPermissionForModuleTypeInAnySurvey,
  } = useUserPermissions();
  const [selectedRow, setSelectedRow] = useState<ActionItemSelection>({
    surveyId: null,
    actionId: null,
    type: null,
  });

  const createNewActionItem = () => {
    setSelectedRow({
      surveyId: 0,
      actionId: 0,
      type: 'follow-up',
    });
  };

  const handleSuccessfulCreation = (newActionItemId: number) => {
    setSelectedRow({
      surveyId: 0,
      actionId: newActionItemId,
      type: 'follow-up',
    });
  };

  const onClose = () =>
    setSelectedRow({
      surveyId: null,
      actionId: null,
      type: null,
    });

  return (
    <Box>
      <FollowUpTableProvider>
        <Stack
          direction="row"
          spacing={1.25}
          sx={{
            justifyContent: 'flex-end',
            pb: 2,
          }}
        >
          <FollowUpDetailProvider
            actionId={selectedRow.actionId}
            enable={selectedRow.type === 'follow-up'}
            onSuccessfulCreation={handleSuccessfulCreation}
          >
            <CreateActionItemModalButton
              onConfirm={createNewActionItem}
              disabled={
                !hasWritePermissionForModuleTypeInAnySurvey(
                  SECTION.FOLLOW_UP_ACTION,
                ) &&
                !hasAdminPermissionForModuleTypeInAnySurvey(
                  SECTION.FOLLOW_UP_ACTION,
                )
              }
            />
            <Button onClick={downloadAll}>{t('download')}</Button>
            <FollowUpDetailModal
              actionId={selectedRow.actionId}
              onClose={onClose}
              enable={selectedRow.type === 'follow-up'}
              onRowSelect={setSelectedRow}
            />
          </FollowUpDetailProvider>
        </Stack>
        <TicketDetailModal
          selectedTicketId={selectedRow.actionId}
          surveyId={selectedRow.surveyId}
          onClose={onClose}
          enable={selectedRow.type === 'ticket'}
          onRowSelect={setSelectedRow}
        />
        <FollowUpTable
          onRowSelect={(selectedRow: ActionItemSelection) =>
            setSelectedRow(selectedRow)
          }
        />
      </FollowUpTableProvider>
    </Box>
  );
};

export default FollowUpOverview;
